import { useState, useEffect, useCallback, useRef } from 'react'

import axios from '../../../api/apiClient';
// icons
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import CampaignIcon from '@mui/icons-material/Campaign'; // Announcement
import DescriptionIcon from '@mui/icons-material/Description'; // Memo
import BadgeIcon from '@mui/icons-material/Badge'; // Employee Memo

import { domain, token } from '../../Security';
import { LoadingSpinner } from '../../../common/Loading';

export function CalendarBody({ selectedDate,
  date,
  setDate,
  addEventsAnnouncementHandlerClick,
  addEventsMemoHandlerClick,
  addEventsEmployeeMemoHandlerClick,
  isRefreshEvents,
  setIsRefreshEvents,
}) {
  const currentDate = selectedDate;
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const firstDayOfWeek = firstDayOfMonth.getDay();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const [calendarGrid, setCalendarGrid] = useState([]);

  const [activeDate, setActiveDate] = useState("");

  const [announcements, setAnnouncements] = useState([]);
  const [memos, setMemos] = useState([]);
  const [employeeMemos, setEmployeeMemos] = useState([]);
  const [loadingRequest, setLoadingRequest] = useState(false);

  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isToggleOpen, setIsToggleOpen] = useState(false);
  const [toggleDate, setToggleDate] = useState(false);

  // Ref for the popup
  const popupRef = useRef(null);

  // Handle clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isToggleOpen && popupRef.current && !popupRef.current.contains(event.target)) {
        if (!event.target.closest('.toggle-btn-trigger')) {
          setIsToggleOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isToggleOpen]);

  const fetchDataAnnouncement = useCallback(async () => {
    setLoadingRequest(true);
    const apiUrl = domain + "/api/Announcement";
    try {
      const response = await axios.get(`${apiUrl}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data;
    } catch (error) {
      setLoadingRequest(false);
      console.error('Error fetching data:', error);
      return [];
    } finally {
      setLoadingRequest(false);
    }
  }, []);

  const fetchDataMemo = useCallback(async () => {
    const apiUrl = domain + "/api/Memo";
    try {
      const response = await axios.get(`${apiUrl}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data;
    } catch (error) {
      console.error('Error fetching data:', error);
      return [];
    } finally {
      setIsRefreshEvents(false);
    }
  }, [setIsRefreshEvents]);

  const fetchDataEmployeeMemo = useCallback(async () => {
    const apiUrl = domain + "/api/EmployeeMemo";
    try {
      const response = await axios.get(`${apiUrl}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return response.data;
    } catch (error) {
      console.error('Error fetching employee memos:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    const fetchDataGeneral = async () => {
      const fetchedAnnouncement = await fetchDataAnnouncement();
      const fetchedMemos = await fetchDataMemo();
      const fetchedEmployeeMemos = await fetchDataEmployeeMemo();

      setAnnouncements(fetchedAnnouncement);
      setMemos(fetchedMemos);
      setEmployeeMemos(fetchedEmployeeMemos);
    };

    fetchDataGeneral();
  }, [fetchDataAnnouncement, fetchDataMemo, fetchDataEmployeeMemo, isRefreshEvents]);

  function formatDate(dateString) {
    const date = new Date(dateString);
    const utcTime = date.getTime() - (date.getTimezoneOffset() * 60000);
    const utcDate = new Date(utcTime);
    const year = utcDate.getUTCFullYear();
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function areDatesEqual(date1, date2) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  const getAnnouncementEvents = useCallback((date) => {
    let haveEvents = false;
    let length = 0;
    announcements.forEach(announcement => {
      const announcementDateFrom = new Date(announcement.dateFrom);
      const announcementDateTo = new Date(announcement.dateTo);
      if (date >= announcementDateFrom && date <= announcementDateTo) {
        haveEvents = true;
        length++;
      }
    });
    return { haveEvents, length };
  }, [announcements]);

  const getMemoEvents = useCallback((date) => {
    let haveEvents = false;
    let length = 0;
    memos.forEach(memo => {
      const memoDateFrom = new Date(memo.dateFrom);
      const memoDateTo = new Date(memo.dateTo);
      if (date >= memoDateFrom && date <= memoDateTo) {
        haveEvents = true;
        length++;
      }
    })
    return { haveEvents, length };
  }, [memos]);

  const getEmployeeMemoEvents = useCallback((date) => {
    let haveEvents = false;
    let length = 0;
    employeeMemos.forEach(memo => {
      const memoDateFrom = new Date(memo.dateFrom);
      const memoDateTo = new Date(memo.dateTo);
      if (date >= memoDateFrom && date <= memoDateTo) {
        haveEvents = true;
        length++;
      }
    });
    return { haveEvents, length };
  }, [employeeMemos]);

  const handleClick = useCallback((date, divKey) => {
    setActiveDate(divKey);
    setDate(formatDate(date));
  }, [setDate]);

  const toggleButtonClick = useCallback((date) => {
    setIsToggleOpen(!isToggleOpen);
    setToggleDate(date);
  }, [isToggleOpen]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1500px)');
    const handleMediaQueryChange = (event) => {
      setIsSmallScreen(event.matches);
    };
    handleMediaQueryChange(mediaQuery);
    mediaQuery.addEventListener('change', handleMediaQueryChange);
    return () => {
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, []);

  useEffect(() => {
    const buildCalendarGrid = () => {
      const newCalendarGrid = [];

      const prevMonthLastDate = new Date(currentYear, currentMonth, 0).getDate();
      const prevMonthStartDay = prevMonthLastDate - firstDayOfWeek + 1;

      for (let i = prevMonthStartDay; i <= prevMonthLastDate; i++) {
        newCalendarGrid.push(
          <div key={`prev-${i}`} className="calendar-cell prev-month">{`${new Date(currentYear, currentMonth - 1).toLocaleString('default', { month: 'short' })} ${i}`}</div>
        );
      }

      for (let i = 1; i <= daysInMonth; i++) {
        const currentDate = new Date(currentYear, currentMonth, i);

        // CHECK IF IT IS TODAY
        const isToday = currentDate.toDateString() === new Date().toDateString();

        const classNames = ["calendar-cell", "current-month"];
        if (isToday) {
          classNames.push("current-date");
        }

        // Determine background color logic
        let bgColorClass = "bg-white"; // Default
        if (activeDate === `current-${i}`) {
          bgColorClass = "!bg-blue-600 !text-white shadow-inner"; // Active (Override everything)
        } else if (isToday) {
          bgColorClass = "bg-green-300"; // Today (green background)
        }

        newCalendarGrid.push(
          <div
            key={`current-${i}`}
            className={`${classNames.join(" ")} h-20 border border-gray-200 flex flex-col items-center justify-center relative cursor-pointer transition-colors duration-200 hover:bg-green-500 hover:text-white ${bgColorClass}`}
            onClick={() => handleClick(new Date(currentYear, currentMonth, i), `current-${i}`)}
          >
            <div className='w-full h-full flex flex-col items-center justify-center relative'>
              <div className="font-medium">{i}</div>
              {(getAnnouncementEvents(currentDate).haveEvents || getMemoEvents(currentDate).haveEvents || getEmployeeMemoEvents(currentDate).haveEvents) &&
                // Changed to justify-end to align with where the toggle is
                <div className='absolute bottom-1 right-1 flex flex-row justify-end items-end w-full gap-1 px-1'>
                  {!isSmallScreen ?
                    <>
                      {/* DESKTOP VIEW: BIGGER ICONS (w-7 h-7) */}
                      {getAnnouncementEvents(currentDate).haveEvents &&
                        <div
                          className='group relative w-7 h-7 bg-orange-500 hover:bg-bg-orange-600 rounded-md flex items-center justify-center shadow-sm cursor-pointer transition-transform hover:scale-105'
                          onClick={() => addEventsAnnouncementHandlerClick(formatDate(currentDate))}
                          title="Announcement"
                        >
                          {/* Notification Dot */}
                          <div className='absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-red-500 ring-1 ring-white flex items-center justify-center rounded-full text-[8px] font-bold text-white z-10'>
                            {getAnnouncementEvents(currentDate).length}
                          </div>
                          <CampaignIcon className="!w-4 !h-4 text-white" />
                        </div>
                      }
                      {getMemoEvents(currentDate).haveEvents &&
                        <div
                          className='group relative w-7 h-7 bg-[#7b92f1] hover:bg-[#3757d8] rounded-md flex items-center justify-center shadow-sm cursor-pointer transition-transform hover:scale-105'
                          onClick={() => addEventsMemoHandlerClick(formatDate(currentDate))}
                          title="Memo"
                        >
                          <div className='absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-red-500 ring-1 ring-white flex items-center justify-center rounded-full text-[8px] font-bold text-white z-10'>
                            {getMemoEvents(currentDate).length}
                          </div>
                          <DescriptionIcon className="!w-4 !h-4 text-white" />
                        </div>
                      }
                      {getEmployeeMemoEvents(currentDate).haveEvents &&
                        <div
                          className='group relative w-7 h-7 bg-green-500 hover:bg-green-600 rounded-md flex items-center justify-center shadow-sm cursor-pointer transition-transform hover:scale-105'
                          onClick={() => addEventsEmployeeMemoHandlerClick(formatDate(currentDate))}
                          title="Employee Memo"
                        >
                          <div className='absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-red-500 ring-1 ring-white flex items-center justify-center rounded-full text-[8px] font-bold text-white z-10'>
                            {getEmployeeMemoEvents(currentDate).length}
                          </div>
                          <BadgeIcon className="!w-4 !h-4 text-white" />
                        </div>
                      }
                    </>
                    :
                    <>
                      {/* MOBILE/TOGGLE VIEW */}
                      <div className='bg-blue-400 text-white p-0.5 rounded-lg w-5 h-5 flex items-center justify-center shadow-sm hover:bg-blue-500 cursor-pointer transition-colors toggle-btn-trigger' onClick={() => toggleButtonClick(formatDate(currentDate))}>
                        <CalendarViewDayIcon className='!w-3.5 !h-3.5 pointer-events-none' />
                      </div>

                      {isToggleOpen && (areDatesEqual(toggleDate, formatDate(currentDate))) &&
                        // POPUP MENU: Vertical list of Icons Only
                        <div ref={popupRef} className="absolute bg-white -right-[-28px] -bottom-[60px] border border-gray-200 rounded-lg p-1.5 z-50 shadow-xl flex flex-col gap-2 text-left">
                          {getAnnouncementEvents(currentDate).haveEvents &&
                            <div
                              className='relative w-7 h-7 bg-orange-500 hover:bg-bg-orange-600 rounded-md flex items-center justify-center shadow-sm cursor-pointer transition-colors'
                              onClick={() => addEventsAnnouncementHandlerClick(formatDate(currentDate))}
                              title="Announcement"
                            >
                              <div className='absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-red-500 ring-1 ring-white flex items-center justify-center rounded-full text-[8px] font-bold text-white'>
                                {getAnnouncementEvents(currentDate).length}
                              </div>
                              <CampaignIcon className="!w-4 !h-4 text-white" />
                            </div>
                          }
                          {getMemoEvents(currentDate).haveEvents &&
                            <div
                              className='relative w-7 h-7 bg-[#7b92f1] hover:bg-[#3757d8] rounded-md flex items-center justify-center shadow-sm cursor-pointer transition-colors'
                              onClick={() => addEventsMemoHandlerClick(formatDate(currentDate))}
                              title="Memo"
                            >
                              <div className='absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-red-500 ring-1 ring-white flex items-center justify-center rounded-full text-[8px] font-bold text-white'>
                                {getMemoEvents(currentDate).length}
                              </div>
                              <DescriptionIcon className="!w-4 !h-4 text-white" />
                            </div>
                          }
                          {getEmployeeMemoEvents(currentDate).haveEvents &&
                            <div
                              className='relative w-7 h-7 bg-green-500 hover:bg-green-600 rounded-md flex items-center justify-center shadow-sm cursor-pointer transition-colors'
                              onClick={() => addEventsEmployeeMemoHandlerClick(formatDate(currentDate))}
                              title="Employee Memo"
                            >
                              <div className='absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-red-500 ring-1 ring-white flex items-center justify-center rounded-full text-[8px] font-bold text-white'>
                                {getEmployeeMemoEvents(currentDate).length}
                              </div>
                              <BadgeIcon className="!w-4 !h-4 text-white" />
                            </div>
                          }
                        </div>
                      }
                    </>
                  }
                </div>
              }
            </div>
          </div>
        );
      }

      // Push dates for next month
      const totalGridCells = 7 * Math.ceil((daysInMonth + firstDayOfWeek) / 7);
      for (let i = 1; newCalendarGrid.length < totalGridCells; i++) {
        const nextMonthDate = i <= daysInMonth ? i : i - daysInMonth;
        const nextMonth = currentMonth + 1;
        const nextYear = nextMonth === 12 ? currentYear + 1 : currentYear;
        const nextMonthName = nextMonth === 12 ? new Date(nextYear, 0).toLocaleString('default', { month: 'short' }) : new Date(nextYear, nextMonth).toLocaleString('default', { month: 'short' });
        newCalendarGrid.push(
          <div key={`next-${i}`} className="calendar-cell next-month">{`${nextMonthName} ${nextMonthDate}`}</div>
        );
      }

      setCalendarGrid(newCalendarGrid);
    };

    buildCalendarGrid();
  }, [isSmallScreen,
    toggleButtonClick,
    isToggleOpen,
    toggleDate,
    announcements,
    memos,
    activeDate,
    currentMonth,
    currentYear,
    daysInMonth,
    firstDayOfWeek,
    addEventsAnnouncementHandlerClick,
    addEventsMemoHandlerClick,
    addEventsEmployeeMemoHandlerClick,
    handleClick,
    getAnnouncementEvents,
    getMemoEvents,
    getEmployeeMemoEvents]);

  return (
    <div>
      <div className="calendar-grid">
        {weekDays.map(day => (
          <div key={day} className="calendar-cell day-of-week">{day}</div>
        ))}
        {calendarGrid}
      </div>
      <LoadingSpinner loading={loadingRequest} />
    </div>
  )
}